<script lang="ts" setup>
import { ref } from 'vue'

import type { Props } from './types'
import type { ImsUnitQuotationPlans } from '@/server/api/buyer-front/types'
import { getMonthlyPayments } from '@/components/QuotationPlan/utils'

defineProps<Props>()

const { t, n } = useI18n()
const emit = defineEmits(['openDetails'])

const monthlyPaymentsValue = (quotationPlan: ImsUnitQuotationPlans) => {
  const monthlyPayments = getMonthlyPayments(quotationPlan.monthly_payments_and_additions)
  const lowestMontlhyPayment = monthlyPayments.sort((a, b) => a.value - b.value).find(item => item.value)

  return lowestMontlhyPayment?.value ?? 0
}

const resolveMonthlyPaymentsValue = (quotationPlan: ImsUnitQuotationPlans) => {
  const monthlyPaymentPrice = quotationPlan.summary_final_plan_items.monthly_payment_price.value
  const monthlyPaymentsCount = monthlyPaymentPrice ? getMonthlyPayments(quotationPlan.monthly_payments_and_additions).length : 0

  return monthlyPaymentsValue(quotationPlan)
    ? t('monthlyPaymentsValue', { monthly: monthlyPaymentsCount, price: n(monthlyPaymentsValue(quotationPlan), 'currency') })
    : t('noApply')
}

const formatAsCurrency = (value: number) => n(value, 'currency')

const quotationRows = ref([
  {
    label: 'downpayment',
    valueFunction: (quotationPlan: ImsUnitQuotationPlans) => formatAsCurrency(quotationPlan.summary_final_plan_items.initial_fee_price.value + quotationPlan.summary_final_plan_items.separation_price.value)
  },
  {
    label: 'monthlyPayments',
    valueFunction: (quotationPlan: ImsUnitQuotationPlans) => resolveMonthlyPaymentsValue(quotationPlan)
  },
  {
    label: 'endPayment',
    valueFunction: (quotationPlan: ImsUnitQuotationPlans) => formatAsCurrency(quotationPlan.summary_final_plan_items.credit_price.value)
  },
  {
    label: 'totalPrice',
    valueFunction: (quotationPlan: ImsUnitQuotationPlans) => formatAsCurrency(quotationPlan.property_price)
  }
])
</script>

<script lang="ts">
export default {
  name: 'AppTablePayment'
}
</script>

<template>
  <div>
    <AppTable class="app-table-payment border border-carbon-light-50">
      <template #header>
        <div class="table-cell w-130 min-w-130 px-8 text-12 text-left align-middle bg-white border-b border-carbon-light-50 sticky left-0">
          <span class="text-primary-800 font-medium">
            {{ t('pricesDescription') }}
          </span>
        </div>

        <div
          v-for="(quotationPlan, index) in quotationPlans"
          :key="`quotation-plan-${index}`"
          class="app-table-payment__header table-cell w-130 pl-26 pr-8 py-16 text-end bg-primary-50 border-b border-carbon-light-50">
          <p class="text-20 text-carbon-800 font-bold whitespace-nowrap">
            {{ t('planCount', { count: index + 1 }) }}
          </p>

          <button
            id="app-table-payment-view-details-button"
            :data-lh-id="`quotation-plan-table-button-view-plan-details-${index}`"
            type="button"
            :aria-label="t('viewDetails')"
            class="text-14 text-primary-600 font-medium underline whitespace-nowrap"
            @click="emit('openDetails', quotationPlan.payment_plan_config.id)">
            {{ t('viewDetails') }}
          </button>
        </div>
      </template>

      <template #content>
        <div
          v-for="row in quotationRows"
          :key="row.label"
          class="app-table-payment__content-row table-row">
          <p class="table-cell pl-16 pr-8 text-14 text-carbon-600 bg-white sticky left-0 border-b border-carbon-light-50">
            {{ t(row.label) }}
          </p>

          <p
            v-for="(quotationPlan, index) in quotationPlans"
            :key="index"
            class="table-cell px-8 py-12 text-14 text-end text-carbon-800 whitespace-nowrap border-b border-carbon-light-50">
            {{ row.valueFunction(quotationPlan) }}
          </p>
        </div>
      </template>
    </AppTable>

    <div class="pt-24">
      <slot name="contact-form" />
    </div>
  </div>
</template>

<style lang="scss">
.app-table-payment {
  &__content-row {
    grid-column: 1 / -1;

    &:nth-child(2) p {
      @apply text-primary-600;
    }

    &:nth-last-child(1) p {
      @apply bg-primary-50 font-medium;
    }
  }

  &__header,
  &__content-row p {
    &:nth-last-child(1) {
      @apply pr-24;
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
